import { useCallback, useMemo } from 'react';
import { useResolveRoute } from '@mews/routing';
import { RouteName, TextKey } from '@mews-commander/enums';
import { DropdownItem } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { openModal } from '../../../../app';
import { ProductCheckListRowAction } from '../../../containers/ProductCheckListTable/types';
import { ProductCheckListDataRow } from '../../../types';
import { getProductOrderCreateUrl } from '../../../../billing/actions/productOrders';
import { showReservationDetail } from '../../../../routes';
import { ReservationTab } from '../../../../../types/reservationTab';

export const useActionCellGetOptionDropdownItem = (
    row: ProductCheckListDataRow,
    setPanel: (panel: ProductCheckListRowAction) => void
) => {
    const resolveRoute = useResolveRoute();
    const dispatch = useCommanderThunkDispatch();

    const viewGuest = useCallback(() => {
        const guest = row.reservation ? row.guests[0] : row.owner;
        if (guest) {
            const profileUrl = resolveRoute(RouteName.CustomerDetail, { id: guest.id });
            dispatch(openModal(profileUrl));
        }
    }, [dispatch, resolveRoute, row]);

    const viewReservation = useCallback(() => {
        if (row.reservation) {
            dispatch(
                showReservationDetail({ reservationId: row.reservation.id, reservationTab: ReservationTab.Items })
            );
        }
    }, [dispatch, row.reservation]);

    const openAddProductsToGuest = useCallback(() => {
        const { owner, guests } = row;
        if (owner) {
            const url = getProductOrderCreateUrl(resolveRoute)({
                ownerId: owner.id,
                guestIds: guests.filter(({ id }) => id !== owner.id).map(({ id }) => id),
                allowNegativeValues: false,
                onlyOfferedToEmployee: true,
            });
            dispatch(openModal(url));
        }
    }, [row, dispatch, resolveRoute]);

    const options: Record<ProductCheckListRowAction, React.ReactNode> = useMemo(
        () => ({
            [ProductCheckListRowAction.ClaimPerPerson]: (
                <DropdownItem
                    key={ProductCheckListRowAction.ClaimPerPerson}
                    label={lt(TextKey.ClaimPerPerson)}
                    onClick={() => setPanel(ProductCheckListRowAction.ClaimPerPerson)}
                    data-test-claim-per-person
                />
            ),
            [ProductCheckListRowAction.AddProductsToReservation]: (
                <DropdownItem
                    key={ProductCheckListRowAction.AddProductsToReservation}
                    label={lt(TextKey.AddProductsToReservation)}
                    onClick={() => setPanel(ProductCheckListRowAction.AddProductsToReservation)}
                    data-test-add-product-to-reservation
                />
            ),
            [ProductCheckListRowAction.ViewReservation]: (
                <DropdownItem
                    key={ProductCheckListRowAction.ViewReservation}
                    label={lt(TextKey.ViewReservation)}
                    onClick={viewReservation}
                    data-test-view-reservation
                />
            ),
            [ProductCheckListRowAction.ViewGuest]: (
                <DropdownItem
                    key={ProductCheckListRowAction.ViewGuest}
                    label={lt(TextKey.ViewGuest)}
                    onClick={viewGuest}
                    data-test-view-guest
                />
            ),
            [ProductCheckListRowAction.AddProductsToGuests]: (
                <DropdownItem
                    key={ProductCheckListRowAction.AddProductsToGuests}
                    label={lt(TextKey.AddProductsToGuest)}
                    onClick={openAddProductsToGuest}
                    data-test-add-product-to-guest
                />
            ),
        }),
        [openAddProductsToGuest, setPanel, viewGuest, viewReservation]
    );

    return useCallback((option: ProductCheckListRowAction) => options[option], [options]);
};
